import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { mediaUrl } from "@/lib/apiClient";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { LifeBuoy, Search, ChevronRight, ClipboardCheck, Users, Target, MessageCircle, FileText, GraduationCap, HandHeart, Cake, Home, Video, Upload, Trash2, Link2 } from "lucide-react";

const IMG = "/presentation";
const MEMBER_ROLES = ["membro", "visitante"];
const APPROVER_ROLES = ["coordenador", "coordenador_geral", "pastor_senior"];

function ytEmbed(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    let id = "";
    if (u.hostname.includes("youtu.be")) id = u.pathname.slice(1);
    else if (u.pathname.includes("/shorts/")) id = u.pathname.split("/shorts/")[1];
    else if (u.pathname.includes("/embed/")) id = u.pathname.split("/embed/")[1];
    else id = u.searchParams.get("v") || "";
    id = (id || "").split("/")[0].split("?")[0];
    return id ? `https://www.youtube.com/embed/${id}` : null;
  } catch { return null; }
}

const GUIDES = [
  { id: "frequencia", audience: "lider", icon: ClipboardCheck, img: "frequencia", title: "Registrar a frequência", summary: "Marque quem participou do encontro.",
    steps: ["Abra o menu e toque em 'Frequência'.", "Escolha o grupo e a data do encontro.", "Marque os presentes e registre visitantes e oferta.", "Toque em salvar — o histórico aparece abaixo."] },
  { id: "grupo", audience: "lider", icon: Users, img: "grupos", title: "Cadastrar um PGD", summary: "Crie e gerencie um grupo.",
    steps: ["No menu, toque em 'Grupos & Líderes'.", "Toque em 'Novo grupo'.", "Preencha líderes, dia, horário e endereço.", "Salve — o grupo aparece na aba Ativos."] },
  { id: "meta", audience: "lider", icon: Target, img: "metas_lista", title: "Criar uma meta/compromisso", summary: "Registre um combinado e acompanhe.",
    steps: ["Abra 'Metas & Compromissos'.", "Toque em 'Nova meta'.", "Defina título, responsável, prazo e categoria.", "Acompanhe pela Lista ou Kanban e use o Diário."] },
  { id: "atendimento", audience: "lider", icon: MessageCircle, img: "atendimento", title: "Atender pelo WhatsApp", summary: "Fale com os membros rapidamente.",
    steps: ["Abra 'Atendimento'.", "Selecione um ou vários contatos.", "Escolha um modelo de mensagem.", "Toque para abrir o WhatsApp já com o texto pronto."] },
  { id: "relatorio", audience: "lider", icon: FileText, img: "relatorios", title: "Gerar relatório em PDF", summary: "Leve dados para a reunião.",
    steps: ["Abra 'Relatório Pastoral'.", "Escolha o supervisor, líder ou grupo.", "Toque em baixar/gerar PDF.", "Imprima ou arquive o arquivo."] },
  { id: "formacao", audience: "lider", icon: GraduationCap, img: "formacao", title: "Acompanhar a formação", summary: "Estudos do mês e escola.",
    steps: ["Abra 'Estudos & Formação'.", "Escolha um estudo ou módulo.", "Leia o conteúdo e materiais.", "Marque como concluído para registrar o progresso."] },
  { id: "meu-grupo", audience: "membro", icon: Home, img: "grupos", title: "Encontrar o meu PGD", summary: "Veja o grupo mais perto de você.",
    steps: ["Abra 'Grupos & Líderes' (ou a página inicial pública).", "Veja o endereço e o bairro de cada grupo.", "Toque na localização para abrir o mapa.", "Combine a sua presença com o líder."] },
  { id: "oracao", audience: "membro", icon: HandHeart, img: null, title: "Enviar um pedido de oração", summary: "Compartilhe e ore junto.",
    steps: ["Abra 'Pedidos de Oração'.", "Escreva o seu pedido.", "Envie — a liderança e o grupo poderão orar.", "Acompanhe os pedidos da comunidade."] },
  { id: "aniversario", audience: "membro", icon: Cake, img: null, title: "Ver os aniversariantes", summary: "Celebre com a família Aprisco.",
    steps: ["Abra 'Aniversariantes'.", "Veja quem faz aniversário no mês.", "Envie os parabéns pelo WhatsApp com um toque."] },
];

export default function HelpCenter() {
  const { user } = useAuth();
  const defaultAud = user && MEMBER_ROLES.includes(user.role) ? "membro" : "lider";
  const [aud, setAud] = useState(defaultAud);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [videos, setVideos] = useState({});
  const isApprover = user && APPROVER_ROLES.includes(user.role);

  const loadVideos = () => api.get("/help/guide-videos").then((r) => setVideos(r.data || {})).catch(() => {});
  useEffect(() => { loadVideos(); }, []);

  const list = useMemo(() => {
    const term = q.trim().toLowerCase();
    return GUIDES.filter((g) => g.audience === aud)
      .filter((g) => !term || g.title.toLowerCase().includes(term) || g.summary.toLowerCase().includes(term));
  }, [aud, q]);

  return (
    <div className="space-y-6 animate-fade-up" data-testid="help-center-page">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight flex items-center gap-3">
          <LifeBuoy className="h-8 w-8 text-primary" /> Central de Ajuda
        </h1>
        <p className="text-muted-foreground mt-1">Guias curtos de "como fazer", com passos e imagens das telas.</p>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <Tabs value={aud} onValueChange={setAud}>
          <TabsList data-testid="help-audience-tabs">
            <TabsTrigger value="lider" data-testid="help-tab-lider">Para líderes</TabsTrigger>
            <TabsTrigger value="membro" data-testid="help-tab-membro">Para membros</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar um guia…" className="pl-9" data-testid="help-search-input" />
        </div>
      </div>

      {list.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground" data-testid="help-empty">Nenhum guia encontrado.</Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="help-guides">
          {list.map((g) => (
            <button key={g.id} onClick={() => setSel(g)} data-testid={`help-guide-${g.id}`}
              className="text-left rounded-2xl border border-border bg-card p-5 hover:border-primary/40 hover:shadow-md transition-all group">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary mb-3 group-hover:scale-105 transition-transform">
                <g.icon className="h-5 w-5" />
              </span>
              <div className="font-semibold">{g.title}</div>
              <p className="text-sm text-muted-foreground mt-1">{g.summary}</p>
              <span className="mt-3 inline-flex items-center gap-2">
                <span className="inline-flex items-center gap-1 text-sm font-medium text-primary">Ver passos <ChevronRight className="h-4 w-4" /></span>
                {videos[g.id] && <Badge variant="outline" className="gap-1 text-[10px]"><Video className="h-3 w-3" /> vídeo</Badge>}
              </span>
            </button>
          ))}
        </div>
      )}

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-lg" data-testid="help-guide-dialog">
          {sel && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><sel.icon className="h-5 w-5 text-primary" /> {sel.title}</DialogTitle>
                <DialogDescription>{sel.summary}</DialogDescription>
              </DialogHeader>
              <ol className="space-y-3 mt-1" data-testid="help-guide-steps">
                {sel.steps.map((st, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="shrink-0 h-6 w-6 rounded-full bg-primary text-primary-foreground text-xs font-bold inline-flex items-center justify-center">{i + 1}</span>
                    <span className="text-sm pt-0.5">{st}</span>
                  </li>
                ))}
              </ol>
              {sel.img && (
                <div className="mt-4 rounded-xl border border-border overflow-hidden">
                  <img src={`${IMG}/${sel.img}.png`} alt={sel.title} className="w-full h-auto block" loading="lazy" />
                </div>
              )}
              <GuideVideo guide={sel} video={videos[sel.id]} isApprover={isApprover} onChange={loadVideos} />
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function GuideVideo({ guide, video, isApprover, onChange }) {
  const [editing, setEditing] = useState(false);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const saveYoutube = async () => {
    if (!url.trim()) return toast.error("Cole o link do YouTube");
    setBusy(true);
    try { await api.put(`/help/guide-videos/${guide.id}`, { url: url.trim() }); toast.success("Vídeo salvo"); setEditing(false); setUrl(""); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setBusy(false); }
  };
  const uploadMp4 = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      await api.post(`/help/guide-videos/${guide.id}/upload`, fd);
      toast.success("Vídeo enviado"); onChange();
    } catch (err) { toast.error(err.response?.data?.detail || "Falha no upload"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const remove = async () => {
    setBusy(true);
    try { await api.delete(`/help/guide-videos/${guide.id}`); toast.success("Vídeo removido"); onChange(); }
    catch { toast.error("Erro"); }
    finally { setBusy(false); }
  };

  const embed = video?.type === "youtube" ? ytEmbed(video.url) : null;

  return (
    <div className="mt-4" data-testid="guide-video-section">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><Video className="h-3.5 w-3.5" /> Vídeo</span>
        {isApprover && video && !editing && (
          <button onClick={remove} disabled={busy} className="text-xs text-destructive hover:underline inline-flex items-center gap-1" data-testid="guide-video-remove"><Trash2 className="h-3 w-3" /> Remover</button>
        )}
      </div>

      {video ? (
        <div className="rounded-xl overflow-hidden border border-border bg-black" data-testid="guide-video-player">
          {embed ? (
            <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
              <iframe title={guide.title} src={embed} className="absolute inset-0 w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            </div>
          ) : video.type === "mp4" ? (
            <video src={mediaUrl(video.path)} controls className="w-full h-auto block" />
          ) : (
            <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
              <iframe title={guide.title} src={video.url} className="absolute inset-0 w-full h-full" allowFullScreen />
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-border bg-secondary/40 py-6 text-center text-sm text-muted-foreground" data-testid="guide-video-placeholder">
          🎬 Vídeo em breve
        </div>
      )}

      {isApprover && (
        <div className="mt-3">
          {editing ? (
            <div className="flex flex-col sm:flex-row gap-2">
              <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Cole o link do YouTube…" data-testid="guide-video-url-input" />
              <div className="flex gap-2">
                <Button size="sm" onClick={saveYoutube} disabled={busy} data-testid="guide-video-save">Salvar</Button>
                <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setUrl(""); }}>Cancelar</Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setEditing(true)} data-testid="guide-video-youtube-btn"><Link2 className="h-4 w-4" /> {video ? "Trocar link" : "Adicionar link do YouTube"}</Button>
              <Button size="sm" variant="outline" className="gap-1.5" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="guide-video-upload-btn"><Upload className="h-4 w-4" /> Enviar .mp4</Button>
              <input ref={fileRef} type="file" accept="video/mp4,video/quicktime,video/webm" className="hidden" onChange={uploadMp4} data-testid="guide-video-file-input" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
